import { useState } from "react";
import { PageMeta } from "../components/PageMeta";
import { CTASection, PageHero, SectionHeading } from "../components/Sections";
import { teamMembers } from "../data/team";
import { useI18n, useLocalizedContent } from "../i18n";

export function AboutPage() {
  const { l, locale } = useI18n();
  const team = useLocalizedContent(teamMembers);
  const [selectedMemberId, setSelectedMemberId] = useState(team[0]?.id ?? "");
  const selectedMember =
    team.find((member) => member.id === selectedMemberId) ?? team[0];
  const specialties = {
    zh: {
      csk: ["产品架构", "AI 原生系统", "全栈交付"],
      cyc: ["AI 与嵌入式", "系统优化", "端云协同"],
      mm: ["云原生平台", "分布式系统", "AI 基础设施", "大模型增强网络安全"],
      xjp: ["智能感知", "信号处理", "IoT 系统"],
      yxm: ["嵌入式系统", "边缘计算", "端云协同"],
      wzl: ["硬件设计", "结构设计", "SolidWorks", "外壳建模", "3D 打印原型"],
    },
    en: {
      csk: ["Product Architecture", "AI-Native Systems", "End-to-End Delivery"],
      cyc: ["AI & Embedded", "System Optimization", "Device–Cloud"],
      mm: [
        "Cloud-Native Platforms",
        "Distributed Systems",
        "AI Infrastructure",
        "LLM-Augmented Cybersecurity",
      ],
      xjp: ["Intelligent Sensing", "Signal Processing", "IoT Systems"],
      yxm: ["Embedded Systems", "Edge Computing", "Device–Cloud"],
      wzl: [
        "Hardware Design",
        "Mechanical Design",
        "SolidWorks",
        "Enclosure Modeling",
        "3D-Printed Prototypes",
      ],
    },
  } as const;
  return (
    <>
      <PageMeta title={l("关于我们", "About")} />
      <PageHero
        className="page-hero--about"
        eyebrow={l("关于加速科学", "ABOUT ACCSCI")}
        title={
          <>
            {locale === "zh" ? (
              <>
                把跨学科工程，
                <br />
              </>
            ) : (
              "Turning cross-disciplinary engineering into "
            )}
            <span className="blue-text">
              {l("变成可以工作的产品。", "products that work.")}
            </span>
          </>
        }
        lead={l(
          "加速科学（AccSci）是一支横跨 AI、软件、电子硬件与产品工程的团队，从快速原型到产品发布、硬件量产和长期运行。",
          "AccSci is a product engineering team spanning AI, software, electronics, and hardware—from rapid prototypes to launches, production, and long-term operation.",
        )}
      />
      <section className="section about-statement dark-section">
        <div className="container about-statement__grid">
          <p className="section-label section-label--inverse">
            {l("我们的理念", "OUR BELIEF")}
          </p>
          <p>
            {l(
              "客户购买的不是技术栈，而是一个可以工作的产品。我们从第一天就让产品体验、模型、软件、电子、结构、制造与运维围绕同一个目标协同。",
              "Clients do not buy a technology stack; they buy a product that works. From day one, product experience, models, software, electronics, mechanical engineering, manufacturing, and operations align around one goal.",
            )}
          </p>
        </div>
      </section>
      <section className="section values-section">
        <div className="container">
          <SectionHeading
            eyebrow={l("工作原则", "HOW WE WORK")}
            title={l(
              "我们如何定义好的产品工程。",
              "What good product engineering means to us.",
            )}
          />
          <div className="values-grid">
            {(locale === "zh"
              ? [
                  [
                    "01",
                    "深入问题",
                    "先理解用户、场景与产品约束，再决定 AI 和硬件应该如何出现。",
                  ],
                  [
                    "02",
                    "系统思考",
                    "模型只是系统的一部分，软件、电子、结构、制造与运维同样重要。",
                  ],
                  [
                    "03",
                    "诚实验证",
                    "用模型评测、板级测试和真实样机代替炫目的演示与模糊承诺。",
                  ],
                  [
                    "04",
                    "长期构建",
                    "选择可以维护、持续升级、稳定生产并被客户团队真正接手的方案。",
                  ],
                ]
              : [
                  [
                    "01",
                    "Go deep on the problem",
                    "Understand the user, context, and product constraints before deciding where AI or hardware belongs.",
                  ],
                  [
                    "02",
                    "Think in systems",
                    "The model is one part; software, electronics, mechanics, production, and operations matter just as much.",
                  ],
                  [
                    "03",
                    "Validate honestly",
                    "Use model evaluation, board-level tests, and working prototypes instead of flashy demos and vague promises.",
                  ],
                  [
                    "04",
                    "Build for the long term",
                    "Choose solutions that can be maintained, upgraded, produced reliably, and owned by the client team.",
                  ],
                ]
            ).map(([index, title, text]) => (
              <article key={index}>
                <span>{index}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="section team-section" id="team">
        <div className="container">
          <SectionHeading
            inverse
            eyebrow={l("团队", "TEAM")}
            title={l("认识我们的核心成员。", "Meet our core team.")}
            description={l(
              "选择一位角色，在同一个视野里查看他的定位、专长与工程经历。",
              "Choose a character to see their role, specialties, and engineering experience in one view.",
            )}
          />
          <div className="team-gameboard">
            {selectedMember ? (
              <article
                className="team-stage"
                id="selected-team-profile"
                key={`${locale}-${selectedMember.id}`}
              >
                <div className="team-stage__backdrop" aria-hidden="true" />
                <div className="team-stage__character" aria-hidden="true">
                  <img
                    className="team-stage__outline"
                    alt=""
                    decoding="async"
                    height="1448"
                    src={selectedMember.cutout}
                    width="1086"
                  />
                  <img
                    className="team-stage__person"
                    alt=""
                    decoding="async"
                    height="1448"
                    src={selectedMember.cutout}
                    width="1086"
                  />
                </div>
                <h3 className="team-stage__display-name">
                  {selectedMember.name}
                </h3>
                <div className="team-stage__hud" aria-hidden="true">
                  <span>
                    {String(
                      team.findIndex(
                        (member) => member.id === selectedMember.id,
                      ) + 1,
                    ).padStart(2, "0")}
                  </span>
                  <i />
                  {l("角色档案已载入", "PROFILE LOADED")}
                </div>
                <div className="team-stage__copy">
                  <span className="team-stage__kicker">
                    {l("当前角色", "CURRENT CHARACTER")}
                  </span>
                  <p className="team-stage__role">{selectedMember.role}</p>
                  {selectedMember.credential ? (
                    <small>{selectedMember.credential}</small>
                  ) : null}
                  <div className="team-stage__tags">
                    {specialties[locale][
                      selectedMember.id as keyof (typeof specialties)[typeof locale]
                    ]?.map((specialty) => (
                      <span key={specialty}>{specialty}</span>
                    ))}
                  </div>
                  <p className="team-stage__bio">{selectedMember.biography}</p>
                </div>
              </article>
            ) : null}
            <ul
              className="team-roster"
              aria-label={l("团队成员选择", "Choose a team member")}
            >
              {team.map((member, index) => {
                const active = member.id === selectedMember?.id;
                return (
                  <li
                    className={`team-card ${active ? "team-card--active" : ""}`}
                    key={member.id}
                  >
                    <button
                      type="button"
                      className="team-card__selector"
                      aria-pressed={active}
                      aria-controls="selected-team-profile"
                      onClick={() => setSelectedMemberId(member.id)}
                      onFocus={() => setSelectedMemberId(member.id)}
                      onPointerEnter={(event) => {
                        if (event.pointerType !== "touch") {
                          setSelectedMemberId(member.id);
                        }
                      }}
                    >
                      <span className="team-card__index">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="team-card__select">
                        {active ? l("已选择", "SELECTED") : l("选择", "SELECT")}
                      </span>
                      <span className="team-card__portrait">
                        <img
                          alt=""
                          decoding="async"
                          height="1448"
                          loading="lazy"
                          src={member.cutout}
                          width="1086"
                        />
                        <i className="team-card__scan" aria-hidden="true" />
                      </span>
                      <span className="team-card__summary">
                        <strong>{member.name}</strong>
                        <small>{member.role}</small>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );
}
